import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight, Check, Settings, FileText, Link2, MapPin, ClipboardList } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/layout/Breadcrumbs';
import DecisionTree from '../components/DecisionTree';

export default function SeoPakketten() {
  const { language } = useLanguage();
  const basePath = language === 'en' ? '/en' : '';

  const sharedFeatures = language === 'nl' ? [
    'Technische SEO-audit en optimalisatie',
    'Zoekwoordenonderzoek en contentstrategie',
    'Content gap-analyse en pruning',
    'Linkbuilding strategie',
    'Google Business Profile (indien relevant)',
    'Concurrentieanalyse',
    'Customer journey mapping',
    'Werkdocument met alle actiepunten',
    'Rapportage per kwartaal',
  ] : [
    'Technical SEO audit and optimization',
    'Keyword research and content strategy',
    'Content gap analysis and pruning',
    'Link building strategy',
    'Google Business Profile (if relevant)',
    'Competitor analysis',
    'Customer journey mapping',
    'Work document with all action items',
    'Quarterly reporting',
  ];

  const packages = [
    {
      name: 'Basic',
      focus: language === 'nl' ? 'De basis op orde' : 'Getting the basics right',
      ideal: language === 'nl' ? 'Lokale spelers & zelfstandig ondernemers' : 'Local businesses & solo entrepreneurs',
      hours: '8',
      price: '720',
      discount: '5%',
      color: 'border-gray-200',
      description: language === 'nl'
        ? 'Het volledige traject, maar in een rustig tempo. Ideaal als je markt minder competitief is of als je team zelf veel kan oppakken op basis van mijn strategische richting.'
        : 'The full journey, but at a steady pace. Ideal if your market is less competitive or if your team can handle a lot based on my strategic direction.',
      speed: language === 'nl' ? 'Rustig tempo' : 'Steady pace',
    },
    {
      name: 'Pro',
      focus: language === 'nl' ? 'Marktaandeel vergroten' : 'Growing market share',
      ideal: language === 'nl' ? 'Ambitieuze MKB-bedrijven' : 'Ambitious SMBs',
      hours: '16',
      price: '1.368',
      discount: '10%',
      color: 'border-[#4286F4]',
      popular: true,
      description: language === 'nl'
        ? 'Meer strategische capaciteit om dieper te duiken in de customer journey en concurrentie. Ik stuur je team of mijn partners actiever aan voor snellere resultaten.'
        : 'More strategic capacity to dive deeper into the customer journey and competition. I more actively direct your team or my partners for faster results.',
      speed: language === 'nl' ? 'Versneld' : 'Accelerated',
    },
    {
      name: 'Authority',
      focus: language === 'nl' ? 'Dominantie in jouw niche' : 'Dominating your niche',
      ideal: language === 'nl' ? 'Gevestigde MKB-bedrijven & specialisten' : 'Established SMBs & specialists',
      hours: '20',
      price: '1.615',
      discount: '15%',
      color: 'border-[#08080A]',
      description: language === 'nl'
        ? 'Maximale strategische inzet en intensiteit. Voor competitieve markten waar je op alle fronten tegelijk wilt bewegen.'
        : 'Maximum strategic effort and intensity. For competitive markets where you want to move on all fronts simultaneously.',
      speed: language === 'nl' ? 'Maximale snelheid' : 'Maximum speed',
    }
  ];

  const inclusions = [
    {
      icon: <Settings className="h-5 w-5" />,
      title: language === 'nl' ? 'Technische optimalisatie' : 'Technical optimization',
      desc: language === 'nl' ? 'Van crawlability tot site-architectuur.' : 'From crawlability to site architecture.'
    },
    {
      icon: <FileText className="h-5 w-5" />,
      title: language === 'nl' ? 'Contentstrategie' : 'Content strategy',
      desc: language === 'nl' ? 'Customer journey mapping, content gap-analyse en pruning.' : 'Customer journey mapping, content gap analysis and pruning.'
    },
    {
      icon: <Link2 className="h-5 w-5" />,
      title: language === 'nl' ? 'Autoriteit' : 'Authority',
      desc: language === 'nl' ? 'Strategische linkbuilding-plannen op basis van concurrentie.' : 'Strategic link building plans based on competition.'
    },
    {
      icon: <MapPin className="h-5 w-5" />,
      title: 'Local SEO',
      desc: language === 'nl' ? 'Optimalisatie voor lokale zichtbaarheid (indien relevant).' : 'Optimization for local visibility (if relevant).'
    },
    {
      icon: <ClipboardList className="h-5 w-5" />,
      title: language === 'nl' ? 'Het Werkdocument' : 'The Work Document',
      desc: language === 'nl' ? 'Inzicht in alle actiepunten, prioriteiten en de status.' : 'Insight into all action items, priorities and status.'
    }
  ];

  return (
    <div data-testid="seo-pakketten-page">
      <SEOHead
        title={language === 'nl' ? 'SEO Pakketten | Holistische SEO in 3 maanden sprints' : 'SEO packages | Holistic Dutch market SEO from €720/month'}
        description={language === 'nl'
          ? 'Vergelijk de SEO-pakketten van Kesult: Basic (8 uur), Pro (16 uur) en Authority (20 uur). Alle SEO-pijlers in balans — verschil zit in snelheid. Bekijk welk pakket bij je past.'
          : 'Compare SEO packages for the Dutch market: Basic (8h), Pro (16h) and Authority (20h). All pillars covered, the difference is speed. Find the right fit with our package guide.'}
        canonical="https://www.kesult.com/seo-pakketten"
        lang={language}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          "name": "Kesult",
          "description": "Freelance SEO Specialist - Holistische SEO pakketten",
          "url": "https://www.kesult.com/seo-pakketten",
          "provider": {
            "@type": "Person",
            "name": "Falco Keiman",
            "jobTitle": "Freelance SEO Specialist"
          },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "SEO Pakketten",
            "itemListElement": [
              { "@type": "Offer", "name": "Basic", "price": "720", "priceCurrency": "EUR", "description": "8 uur per maand - holistische SEO aanpak" },
              { "@type": "Offer", "name": "Pro", "price": "1025", "priceCurrency": "EUR", "description": "16 uur per maand - versnelde groei" },
              { "@type": "Offer", "name": "Authority", "price": "1615", "priceCurrency": "EUR", "description": "20 uur per maand - maximale intensiteit" }
            ]
          }
        }}
      />
      {/* Hero */}
      <section className="relative bg-[#0B1A2E] text-white overflow-hidden" data-testid="pakketten-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-32 lg:pb-40 relative z-10">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold text-[#4286F4] uppercase tracking-widest mb-5">
              {language === 'nl' ? 'Diensten & Prijzen' : 'Services & Pricing'}
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-bold leading-tight mb-6" data-testid="pakketten-title">
              {language === 'nl' ? 'SEO Pakketten' : 'SEO Packages'}
            </h1>
            <p className="text-lg text-white/70 leading-relaxed max-w-2xl">
              {language === 'nl'
                ? 'Een holistische aanpak waarbij alle pijlers in balans zijn. De pakketten verschillen enkel in snelheid en intensiteit — hoe competitiever de markt, hoe meer strategische uren er nodig zijn.'
                : 'A holistic approach where all pillars are in balance. The packages only differ in speed and intensity — the more competitive the market, the more strategic hours are needed.'}
            </p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-white" style={{ clipPath: 'polygon(0 100%, 100% 0, 100% 100%)' }} />
      </section>

      <Breadcrumbs />

      {/* Holistic Intro */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold text-[#4286F4] uppercase tracking-widest mb-3">
              {language === 'nl' ? 'Mijn aanpak' : 'My approach'}
            </p>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#08080A] mb-6">
              {language === 'nl' ? 'Een holistische aanpak' : 'A holistic approach'}
            </h2>
            <div className="space-y-4 text-[#555] leading-relaxed">
              <p>
                {language === 'nl'
                  ? 'Ik geloof niet in losse optimalisaties. Voor structurele groei is een holistische SEO-benadering nodig waarbij alle pijlers (Techniek, Content en Autoriteit) in balans zijn.'
                  : 'I don\'t believe in isolated optimizations. Structural growth requires a holistic SEO approach where all pillars (Technical, Content and Authority) are in balance.'}
              </p>
              <p>
                {language === 'nl'
                  ? 'Mijn werkwijze is voor elke klant gelijk: ik doorloop het volledige traject van A tot Z. Ik analyseer de huidige performance, breng de customer journey in kaart en identificeer content-gaps. Of het nu gaat om het opschonen van oude content, het uitrollen van een technische roadmap of het opzetten van een autoriteitsstrategie: ik regisseer het proces.'
                  : 'My process is the same for every client: I go through the full journey from A to Z. I analyze current performance, map the customer journey and identify content gaps. Whether it\'s cleaning up old content, rolling out a technical roadmap or setting up an authority strategy: I direct the process.'}
              </p>
              <p className="font-semibold text-[#08080A]">
                {language === 'nl'
                  ? 'De pakketten verschillen enkel in snelheid en intensiteit. Hoe competitiever de markt, hoe meer strategische uren er nodig zijn om momentum te creëren en te behouden.'
                  : 'The packages only differ in speed and intensity. The more competitive the market, the more strategic hours are needed to create and maintain momentum.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Packages - "Kies de juiste versnelling" */}
      <section className="py-20 lg:py-28 bg-[#FAFAFA]" data-testid="packages-grid">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-sm font-semibold text-[#4286F4] uppercase tracking-widest mb-3">
            {language === 'nl' ? 'Pakketten' : 'Packages'}
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#08080A] mb-12">
            {language === 'nl' ? 'Kies de juiste versnelling' : 'Choose the right gear'}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {packages.map((pkg) => (
              <div
                key={pkg.name}
                className={`relative rounded-2xl border-2 ${pkg.color} bg-white p-8 flex flex-col ${pkg.popular ? 'shadow-xl' : ''}`}
                data-testid={`package-${pkg.name.toLowerCase()}`}
              >
                {pkg.popular && (
                  <span className="absolute -top-3 left-8 bg-[#4286F4] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                    {language === 'nl' ? 'Populair' : 'Popular'}
                  </span>
                )}

                <h3 className="text-2xl font-bold text-[#08080A] mb-2">{pkg.name}</h3>
                <p className="text-[#4286F4] font-semibold text-sm mb-1">{pkg.focus}</p>
                <p className="text-[#999] text-xs mb-5">{pkg.ideal}</p>

                <div className="bg-[#F1F0EF] rounded-xl p-4 mb-5">
                  <p className="text-3xl font-bold text-[#08080A]">
                    &euro;{pkg.price}<span className="text-sm text-[#999] font-normal"> / {language === 'nl' ? 'mnd' : 'mo'}</span>
                  </p>
                  <p className="text-xs text-[#999] mt-1">
                    {pkg.hours} {language === 'nl' ? 'uur per maand' : 'hours per month'} &middot; {pkg.discount} {language === 'nl' ? 'korting' : 'discount'} &middot; excl. btw
                  </p>
                </div>

                <p className="text-sm text-[#555] leading-relaxed mb-6">{pkg.description}</p>

                <ul className="space-y-3 flex-grow">
                  {sharedFeatures.map((feature, fi) => (
                    <li key={fi} className="flex items-start gap-3">
                      <Check className="h-4 w-4 flex-shrink-0 mt-0.5 text-[#4286F4]" />
                      <span className="text-sm text-[#333]">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link to={`${basePath}/contact`} className="mt-8">
                  <button
                    className={`w-full font-semibold px-6 py-3.5 rounded-full transition-colors inline-flex items-center justify-center gap-2 ${
                      pkg.popular
                        ? 'bg-[#4286F4] text-white hover:bg-[#3070d4]'
                        : 'bg-[#F1F0EF] text-[#08080A] hover:bg-[#E5E4E3]'
                    }`}
                  >
                    {language === 'nl' ? 'Neem contact op' : 'Get in touch'}
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Role as Strategist */}
      <section className="py-20 lg:py-28 bg-[#08080A] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <p className="text-sm font-semibold text-[#4286F4] uppercase tracking-widest mb-3">
              {language === 'nl' ? 'Mijn rol als strateeg' : 'My role as strategist'}
            </p>
            <h2 className="text-2xl sm:text-3xl font-bold mb-6">
              {language === 'nl' ? 'Strategische regie, niet uitvoering' : 'Strategic direction, not execution'}
            </h2>
            <p className="text-[#A1A1AA] leading-relaxed">
              {language === 'nl'
                ? 'Ik werk hoofdzakelijk strategisch. Dat betekent dat ik de strategieën uitwerk en nauw samenwerk met in-house marketeers en developers die het uitvoerende werk doen. Heb je deze specialisten niet in huis? Dan werk ik samen met vaste partners, zoals contentschrijvers en linkbuilding-specialisten. Zo wordt iedereen in zijn kracht gebruikt en focus ik op de regie en het resultaat.'
                : 'I work primarily strategically. This means I develop strategies and closely collaborate with in-house marketers and developers who do the execution. Don\'t have these specialists in-house? Then I work with trusted partners, such as content writers and link building specialists. This way everyone is used to their strengths and I focus on direction and results.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 rounded-2xl border border-white/10">
              <h3 className="font-bold mb-2">{language === 'nl' ? 'Minimale duur' : 'Minimum duration'}</h3>
              <p className="text-[#A1A1AA] text-sm leading-relaxed">
                {language === 'nl'
                  ? '3 maanden (strategische sprint). Per kwartaal opzegbaar tot het einde van de tweede maand.'
                  : '3 months (strategic sprint). Cancellable per quarter until the end of the second month.'}
              </p>
            </div>
            <div className="p-6 rounded-2xl border border-white/10">
              <h3 className="font-bold mb-2">{language === 'nl' ? 'Exclusief uitvoering' : 'Excluding execution'}</h3>
              <p className="text-[#A1A1AA] text-sm leading-relaxed">
                {language === 'nl'
                  ? 'Prijzen zijn exclusief development en het schrijven van content.'
                  : 'Prices exclude development and content writing.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What's included */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-sm font-semibold text-[#4286F4] uppercase tracking-widest mb-3">
            {language === 'nl' ? 'In elk pakket' : 'In every package'}
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#08080A] mb-4">
            {language === 'nl' ? 'Wat zit er in elk pakket?' : 'What\'s included in every package?'}
          </h2>
          <p className="text-[#555] leading-relaxed max-w-2xl mb-12">
            {language === 'nl'
              ? 'Omdat ik holistisch werk, zit de volledige breedte van SEO in elk traject:'
              : 'Because I work holistically, the full breadth of SEO is included in every engagement:'}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {inclusions.map((item, i) => (
              <div key={i} className="p-6 rounded-2xl border border-gray-100 hover:border-[#4286F4]/20 hover:shadow-md transition-all">
                <div className="w-10 h-10 bg-[#4286F4]/10 rounded-xl flex items-center justify-center text-[#4286F4] mb-4">
                  {item.icon}
                </div>
                <h3 className="font-bold text-[#08080A] text-sm mb-2">{item.title}</h3>
                <p className="text-[#555] text-xs leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5-Step Process */}
      <section className="py-20 lg:py-28 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-sm font-semibold text-[#4286F4] uppercase tracking-widest mb-3">
            {language === 'nl' ? 'Hoe het werkt' : 'How it works'}
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#08080A] mb-14">
            {language === 'nl' ? 'In 5 stappen naar resultaat' : 'Results in 5 steps'}
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8">
            {[
              { step: '01', title: language === 'nl' ? 'Vrijblijvende Kennismaking' : 'Free Introduction', desc: language === 'nl' ? 'Een digitale kennismaking om jouw bedrijf, ambities en resources te bespreken.' : 'A digital introduction to discuss your business, ambitions and resources.' },
              { step: '02', title: language === 'nl' ? 'Voorstel' : 'Proposal', desc: language === 'nl' ? 'Advies op basis van analyse, budget en het juiste pakket.' : 'Advice based on analysis, budget and the right package.' },
              { step: '03', title: language === 'nl' ? 'Strategisch Plan' : 'Strategic Plan', desc: language === 'nl' ? 'Een audit op basis van de 3 SEO-pijlers en de huidige status van je website. Daaruit volgt een strategisch plan gefocust op Technical Excellence, Content en Autoriteit.' : 'An audit based on the 3 SEO pillars and the current status of your website. This results in a strategic plan focused on Technical Excellence, Content and Authority.' },
              { step: '04', title: language === 'nl' ? 'SEO Executiefase' : 'SEO Execution Phase', desc: language === 'nl' ? 'Hier wordt de strategie uitgevoerd die uitgewerkt is in het strategisch plan.' : 'This is where the strategy developed in the strategic plan is executed.' },
              { step: '05', title: language === 'nl' ? 'Onderhoudsfase' : 'Maintenance Phase', desc: language === 'nl' ? 'In deze fase wordt alles onderhouden en bijgestuurd.' : 'In this phase, everything is maintained and adjusted.' },
            ].map((item) => (
              <div key={item.step} className="group">
                <span className="text-3xl font-bold text-[#4286F4] block mb-3">{item.step}</span>
                <div className="h-px w-full bg-gray-300 group-hover:bg-[#4286F4] transition-colors mb-4" />
                <h3 className="text-base font-bold text-[#08080A] mb-2">{item.title}</h3>
                <p className="text-sm text-[#555] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Consult */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm font-semibold text-[#4286F4] uppercase tracking-widest mb-3">
                {language === 'nl' ? 'Eenmalig' : 'One-off'}
              </p>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#08080A] mb-6">
                {language === 'nl' ? 'Eenmalig consult' : 'One-time consultation'}
              </h2>
              <p className="text-[#555] leading-relaxed">
                {language === 'nl'
                  ? 'Naast strategische trajecten bied ik gerichte, eenmalige diensten aan voor specifieke vraagstukken. Je ontvangt na afloop een diepgaande analyse met concrete actiepunten waarmee jouw team direct aan de slag kan.'
                  : 'In addition to strategic projects, I offer targeted one-off services for specific issues. You receive an in-depth analysis with concrete action items your team can start working on immediately.'}
              </p>
            </div>
            <div className="flex justify-center lg:justify-end">
              <div className="bg-[#F1F0EF] rounded-2xl p-8 max-w-sm w-full">
                <p className="text-5xl font-bold text-[#08080A] mb-1">&euro;95</p>
                <p className="text-[#999] text-sm mb-4">/ {language === 'nl' ? 'uur' : 'hour'} &middot; excl. btw</p>
                <p className="text-sm text-[#555] mb-6">
                  {language === 'nl'
                    ? 'Eenmalig adviesgesprek, audit of second opinion.'
                    : 'One-time consultation, audit or second opinion.'}
                </p>
                <Link to={`${basePath}/contact`}>
                  <button className="w-full bg-[#4286F4] text-white font-semibold px-6 py-3.5 rounded-full hover:bg-[#3070d4] transition-colors inline-flex items-center justify-center gap-2">
                    {language === 'nl' ? 'Plan een gesprek' : 'Schedule a call'}
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why this works */}
      <section className="py-20 lg:py-28 bg-[#FAFAFA]">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <p className="text-sm font-semibold text-[#4286F4] uppercase tracking-widest mb-3">
            {language === 'nl' ? 'Waarom dit werkt' : 'Why this works'}
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#08080A] mb-6">
            {language === 'nl' ? 'Investeer in een architect, niet in een metselaar' : 'Invest in an architect, not a builder'}
          </h2>
          <p className="text-[#555] leading-relaxed">
            {language === 'nl'
              ? 'Door te kiezen voor strategische regie investeer je in een architect die het overzicht bewaart. Je voorkomt ad-hoc beslissingen en bouwt aan een organisch kanaal dat ook op de lange termijn waarde blijft leveren, zonder vast te zitten aan ondoorzichtige bureau-contracten.'
              : 'By choosing strategic direction, you invest in an architect who maintains oversight. You prevent ad-hoc decisions and build an organic channel that continues to deliver value long-term, without being locked into opaque agency contracts.'}
          </p>
        </div>
      </section>

      {/* Decision Tree */}
      <DecisionTree />

      {/* CTA */}
      <section className="py-16 bg-[#4286F4] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <h2 className="text-2xl sm:text-3xl font-bold">
            {language === 'nl' ? 'Niet zeker welk pakket past?' : 'Not sure which package fits?'}
          </h2>
          <Link to={`${basePath}/contact`}>
            <button className="bg-white text-[#4286F4] font-semibold px-8 py-3.5 rounded-full hover:bg-gray-50 transition-colors inline-flex items-center gap-2">
              {language === 'nl' ? 'Neem contact op' : 'Get in touch'}
              <ArrowRight className="h-4 w-4" />
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
}
